/**
 * Dashboard — Mission Control
 * Poste de commandement du vaisseau Synapse.
 * - 6 KPI stat cards (filtrables)
 * - Onglets de filtre (view-tabs)
 * - Kanban 4 colonnes avec cartes enrichies
 * - Stepper "Nouvelle idée" en 4 étapes
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { api } from "../lib/api";
import { showToast } from "../components/Toast";
import type {
  Mission,
  Project,
  CrewMember,
  ControlMode,
  CrewRole,
  EnvType,
} from "@synapse/shared";

// ── Constantes ────────────────────────────────────────────────────

const STATUS_CONFIG = [
  { key: "idea", label: "Idées", color: "var(--blue)" },
  { key: "plan", label: "En planification", color: "var(--purple)" },
  { key: "validation", label: "En validation", color: "var(--amber)" },
  { key: "done", label: "Terminées", color: "var(--olive)" },
] as const;

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "in_progress", label: "En cours" },
  { key: "waiting", label: "En attente" },
  { key: "done", label: "Terminées" },
  { key: "failed", label: "Échouées" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

type SyncEventResult = {
  label?: string;
  message?: string;
};

type SyncEventDetail = {
  handled?: boolean;
  promise?: Promise<SyncEventResult | void>;
};

const ROLE_ICONS: Record<CrewRole, string> = {
  navigator: "🧭",
  cervelleau: "🧠",
  programmeur: "💻",
  securite: "🛡",
  createur: "🎨",
};

const ROLE_LABELS: Record<CrewRole, string> = {
  navigator: "Navigateur",
  cervelleau: "Cervelleau",
  programmeur: "Programmeur",
  securite: "Sécurité",
  createur: "Créateur",
};

const MODE_LABELS: Record<ControlMode, string> = {
  manual: "MANUEL",
  semi: "SEMI-AUTO",
  auto: "AUTO",
};

const ENV_OPTIONS: EnvType[] = ["dev", "stage", "prod"];

// ── Helpers ───────────────────────────────────────────────────────

/** Détermine si une mission correspond à un filtre de la vue. */
function matchesFilter(mission: Mission, filter: FilterKey): boolean {
  switch (filter) {
    case "all":
      return true;
    case "in_progress":
      return mission.status === "plan" || mission.status === "validation";
    case "waiting":
      return mission.status === "idea";
    case "done":
      return mission.status === "done";
    case "failed":
      return mission.attempt >= mission.maxAttempts && mission.status !== "done";
    default:
      return true;
  }
}

/** Classe CSS pour une pastille d'attempt selon le statut mission. */
function dotClass(mission: Mission): "pass" | "fail" | "" {
  if (mission.status === "done") return "pass";
  if (mission.attempt >= mission.maxAttempts) return "fail";
  return "";
}

function isThisMonth(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
  );
}

// ══════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════

export default function Dashboard() {
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showStepper, setShowStepper] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // ── Drag & Drop ──────────────────────────────────────────────────

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveDragId(e.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (e: DragEndEvent) => {
      const { active, over } = e;
      setActiveDragId(null);
      if (!over) return;

      const missionId = active.id as string;
      const targetId = over.id as string;
      const mission = missions.find((m) => m.id === missionId);
      if (!mission) return;

      // Determine target status
      const validStatuses = ["idea", "plan", "validation", "done"];
      const newStatus = validStatuses.includes(targetId)
        ? targetId
        : missions.find((m) => m.id === targetId)?.status;

      if (!newStatus || newStatus === mission.status) return;

      // Optimistic update
      const prevMissions = missions;
      setMissions((ms) =>
        ms.map((m) =>
          m.id === missionId ? { ...m, status: newStatus as Mission["status"] } : m,
        ),
      );

      try {
        await api.missions.update(missionId, { status: newStatus as Mission["status"] });
        showToast("success", "Mission déplacée", `→ ${STATUS_CONFIG.find((s) => s.key === newStatus)?.label}`);
      } catch {
        // Rollback
        setMissions(prevMissions);
        showToast("error", "Erreur", "Impossible de déplacer la mission");
      }
    },
    [missions],
  );

  // ── Chargement initial ──────────────────────────────────────────

  const loadData = async (isSync = false, throwOnError = false) => {
    try {
      if (isSync) setSyncing(true);
      else setLoading(true);
      const [missionData, projectData, crewData] = await Promise.all([
        api.missions.list(),
        api.projects.list(),
        api.crew.list(),
      ]);
      setMissions(missionData);
      setProjects(projectData);
      setCrew(crewData);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur de chargement";
      setError(message);
      if (throwOnError) {
        throw err instanceof Error ? err : new Error(message);
      }
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Listen for global sync events (from SyncButton)
  useEffect(() => {
    const handleSync = (event: Event) => {
      const syncEvent = event as CustomEvent<SyncEventDetail>;
      if (syncEvent.detail) {
        syncEvent.detail.handled = true;
        syncEvent.detail.promise = loadData(true, true);
      }
    };

    window.addEventListener("synapse:sync", handleSync);
    return () => window.removeEventListener("synapse:sync", handleSync);
  }, []);

  // ── Maps & stats dérivées ───────────────────────────────────────

  const projectMap = useMemo(
    () => new Map(projects.map((p) => [p.id, p])),
    [projects],
  );
  const crewMap = useMemo(
    () => new Map(crew.map((c) => [c.id, c])),
    [crew],
  );

  const stats = useMemo(() => {
    const ideaMissions = missions.filter((m) => m.status === "idea");
    const planMissions = missions.filter((m) => m.status === "plan");
    const validationMissions = missions.filter(
      (m) => m.status === "validation",
    );
    const doneMissions = missions.filter((m) => m.status === "done");

    const ideasReady = ideaMissions.filter((m) => m.description.trim()).length;
    const ideasDrafting = ideaMissions.length - ideasReady;

    const doneThisMonth = doneMissions.filter((m) =>
      isThisMonth(m.updatedAt),
    ).length;

    const successCount = doneMissions.filter(
      (m) => m.attempt <= m.targetAttempts,
    ).length;
    const successRate =
      doneMissions.length > 0
        ? Math.round((successCount / doneMissions.length) * 100)
        : 0;

    const avgAttempts =
      doneMissions.length > 0
        ? Math.round(
            (doneMissions.reduce((acc, m) => acc + m.attempt, 0) /
              doneMissions.length) *
              10,
          ) / 10
        : 0;

    const totalCrew = crew.length;
    const activeCrew = crew.filter((c) => !c.active).length; // inactive = "en mission"

    const evaluatedMissions = missions.filter(
      (m) => m.satisfaction !== null,
    );
    const avgSatisfaction =
      evaluatedMissions.length > 0
        ? Math.round(
            evaluatedMissions.reduce((acc, m) => acc + (m.satisfaction ?? 0), 0) /
              evaluatedMissions.length,
          )
        : 0;

    const failedCount = missions.filter(
      (m) => m.attempt >= m.maxAttempts && m.status !== "done",
    ).length;

    return {
      ideaMissions,
      ideasReady,
      ideasDrafting,
      planCount: planMissions.length,
      validationCount: validationMissions.length,
      doneCount: doneMissions.length,
      doneThisMonth,
      successRate,
      avgAttempts,
      totalCrew,
      activeCrew,
      avgSatisfaction,
      failedCount,
    };
  }, [missions, crew]);

  // ── Missions filtrées par le filtre de vue actif ────────────────

  const filteredMissions = useMemo(
    () => missions.filter((m) => matchesFilter(m, filter)),
    [missions, filter],
  );

  const missionsByStatus = (status: string) =>
    filteredMissions.filter((m) => m.status === status);

  const filterCount = (key: FilterKey): number =>
    missions.filter((m) => matchesFilter(m, key)).length;

  // ── Cartes KPI ─────────────────────────────────────────────────

  const statCards = [
    {
      eyebrow: "Idées en file",
      val: String(stats.ideaMissions.length),
      sub: `${stats.ideasReady} prêtes · ${stats.ideasDrafting} en rédaction`,
      filterKey: "waiting" as FilterKey,
    },
    {
      eyebrow: "Missions actives",
      val: String(stats.planCount + stats.validationCount),
      sub: `${stats.planCount} en exécution · ${stats.validationCount} en QA`,
      filterKey: "in_progress" as FilterKey,
    },
    {
      eyebrow: "Terminées",
      val: String(stats.doneCount),
      sub:
        stats.doneThisMonth > 0
          ? `${stats.doneThisMonth} ce mois-ci`
          : "ce mois-ci",
      filterKey: "done" as FilterKey,
    },
    {
      eyebrow: "Taux de réussite",
      val: `${stats.successRate}%`,
      sub: `moyenne ${stats.avgAttempts} tentatives`,
      filterKey: null,
    },
    {
      eyebrow: "Équipage dispo",
      val: `${stats.activeCrew}/${stats.totalCrew}`,
      sub: `${stats.totalCrew - stats.activeCrew} en mission`,
      filterKey: null,
    },
    {
      eyebrow: "Satisfaction moy",
      val: `${stats.avgSatisfaction}%`,
      sub: "objectif ≥ 90%",
      filterKey: null,
    },
  ];

  // ── Rendu ───────────────────────────────────────────────────────

  return (
    <div className="page active">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="eyebrow">Poste de commandement</div>
          <h1>Mission Control</h1>
          <p>
            Vue d'ensemble du vaisseau Synapse. Idées en file, missions actives,
            statistiques temps réel.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn primary"
            onClick={() => setShowStepper(true)}
          >
            + Nouvelle idée
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="ops-card" style={{ borderColor: "var(--rust)" }}>
          <p style={{ color: "var(--rust)" }}>⚠ {error}</p>
          <button className="btn" onClick={() => loadData()}>Réessayer</button>
        </div>
      )}

      {/* Loading initial */}
      {loading && !error && (
        <div className="loading-overlay show">
          <div className="loading-box">
            <div className="spinner" />
            <div className="loading-text">Chargement des missions…</div>
          </div>
        </div>
      )}

      {/* Spinner de synchro (sans overlay) */}
      {syncing && !loading && (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            className="spinner"
            style={{ width: "20px", height: "20px", borderWidth: "2px" }}
          />
          <span className="loading-text">Synchronisation…</span>
        </div>
      )}

      {/* Stats */}
      {!loading && !error && (
        <div className="stats-row">
          {statCards.map((card, idx) => {
            const isActive =
              card.filterKey !== null && filter === card.filterKey;
            return (
              <div
                key={idx}
                className={`stat-card${isActive ? " stat-active" : ""}`}
                data-stat-filter={card.filterKey ?? undefined}
                onClick={() => {
                  if (card.filterKey !== null) {
                    setFilter(isActive ? "all" : card.filterKey);
                  }
                }}
              >
                <div className="eyebrow">{card.eyebrow}</div>
                <div className="val">{card.val}</div>
                <div className="sub">{card.sub}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter tabs */}
      {!loading && !error && (
        <div className="view-tabs">
          {FILTERS.map((f) => (
            <span
              key={f.key}
              className={`view-tab${filter === f.key ? " active" : ""}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="tab-count">{filterCount(f.key)}</span>
            </span>
          ))}
        </div>
      )}

      {/* Kanban avec Drag & Drop */}
      {!loading && !error && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="kanban">
            {STATUS_CONFIG.map((status) => {
              const columnMissions = missionsByStatus(status.key);
              return (
                <DroppableColumn key={status.key} status={status.key}>
                  <div className="kanban-col-header">
                    <span
                      className="col-dot"
                      style={{ background: status.color }}
                    />
                    {status.label}
                    <span className="col-count">{columnMissions.length}</span>
                  </div>
                  {columnMissions.map((mission) => (
                    <DraggableCard
                      key={mission.id}
                      mission={mission}
                      project={projectMap.get(mission.projectId)}
                      crewMap={crewMap}
                      isDragging={activeDragId === mission.id}
                      onClick={() => navigate(`/mission/${mission.id}`)}
                    />
                  ))}
                  {columnMissions.length === 0 && (
                    <div className="no-results">Aucune mission</div>
                  )}
                </DroppableColumn>
              );
            })}
          </div>
          {/* Drag overlay for smooth visual feedback */}
          <DragOverlay>
            {activeDragId ? (
              (() => {
                const m = missions.find((mm) => mm.id === activeDragId);
                if (!m) return null;
                return (
                  <div style={{ opacity: 0.8, cursor: "grabbing", width: "260px" }}>
                    <KanbanCardContent
                      mission={m}
                      project={projectMap.get(m.projectId)}
                      crewMap={crewMap}
                    />
                  </div>
                );
              })()
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Stepper "Nouvelle idée" */}
      {showStepper && (
        <NewIdeaStepper
          projects={projects}
          crew={crew}
          onClose={() => setShowStepper(false)}
          onCreated={(missionId) => {
            setShowStepper(false);
            navigate(`/mission/${missionId}`);
          }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  DRAG & DROP WRAPPERS
// ══════════════════════════════════════════════════════════════════

function DroppableColumn({
  status,
  children,
}: {
  status: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={`kanban-col${isOver ? " drag-over" : ""}`}
    >
      {children}
    </div>
  );
}

function DraggableCard({
  mission,
  project,
  crewMap,
  isDragging,
  onClick,
}: {
  mission: Mission;
  project?: Project;
  crewMap: Map<string, CrewMember>;
  isDragging: boolean;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: mission.id,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kanban-card${isDragging ? " dragging" : ""}`}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Only navigate if not dragging
        if (!transform) onClick();
        e.stopPropagation();
      }}
    >
      <KanbanCardContent
        mission={mission}
        project={project}
        crewMap={crewMap}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  CARTE KANBAN — contenu (réutilisé par DraggableCard et DragOverlay)
// ══════════════════════════════════════════════════════════════════

function KanbanCardContent({
  mission,
  project,
  crewMap,
}: {
  mission: Mission;
  project?: Project;
  crewMap: Map<string, CrewMember>;
}) {
  // Rôle du premier membre d'équipage assigné
  const firstCrewId = mission.crewIds[0];
  const firstCrew = firstCrewId ? crewMap.get(firstCrewId) : undefined;
  const crewIcon = firstCrew ? ROLE_ICONS[firstCrew.role] : "👤";
  const crewLabel = firstCrew ? ROLE_LABELS[firstCrew.role] : "Non assigné";

  const dc = dotClass(mission);

  return (
    <>
      {/* Titre + Mode chip */}
      <div className="card-top">
        <div className="card-title">{mission.title}</div>
        <span className={`mode-pill ${mission.controlMode}`}>
          {MODE_LABELS[mission.controlMode]}
        </span>
      </div>

      {/* Projet */}
      {project && (
        <div className="card-project">
          <span
            className="pdot"
            style={{ background: project.color || "var(--purple)" }}
          />
          {project.name}
        </div>
      )}

      {/* Description */}
      {mission.description && (
        <div className="card-desc">
          {mission.description.length > 80
            ? mission.description.slice(0, 80) + "…"
            : mission.description}
        </div>
      )}

      {/* Env badges (depuis le projet) */}
      {project && Array.isArray(project.envs) && project.envs.length > 0 && (
        <div className="card-env">
          {project.envs.map((env) => (
            <span key={env} className={`env-badge ${env}`}>
              {env.toUpperCase()}
            </span>
          ))}
        </div>
      )}

      {/* Attempt dots + Progression */}
      {(mission.attempt > 0 || mission.satisfaction !== null) && (
        <div className="card-footer">
          {mission.attempt > 0 && (
            <div className="attempt-dots">
              {Array.from({ length: mission.maxAttempts }).map((_, i) => (
                <span
                  key={i}
                  className={`attempt-dot${i < mission.attempt ? ` ${dc}` : ""}`}
                />
              ))}
            </div>
          )}
          {mission.satisfaction !== null && (
            <span
              style={{
                fontFamily: "var(--mono)",
                fontSize: "11px",
                color: "var(--muted)",
              }}
            >
              {mission.satisfaction}% / {mission.minSatisfaction}%
            </span>
          )}
        </div>
      )}

      {/* Rôle équipage */}
      <div className="card-footer">
        <span style={{ fontSize: "11px", color: "var(--muted)" }}>
          {crewIcon} {crewLabel}
        </span>
      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
//  STEPPER "NOUVELLE IDÉE" — 4 ÉTAPES
// ══════════════════════════════════════════════════════════════════

const STEPS = [
  { num: 1, label: "Idée" },
  { num: 2, label: "Stratégie" },
  { num: 3, label: "Paramètres" },
  { num: 4, label: "Validation" },
] as const;

function NewIdeaStepper({
  projects,
  crew,
  onClose,
  onCreated,
}: {
  projects: Project[];
  crew: CrewMember[];
  onClose: () => void;
  onCreated: (missionId: string) => void;
}) {
  const [step, setStep] = useState(0); // 0-indexé
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Étape 1 — Idée
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState(projects[0]?.id ?? "");

  // Étape 2 — Stratégie
  const [controlMode, setControlMode] = useState<ControlMode>("semi");
  const [skills, setSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [envs, setEnvs] = useState<EnvType[]>(["dev"]);

  // Étape 3 — Paramètres
  const [maxAttempts, setMaxAttempts] = useState(6);
  const [targetAttempts, setTargetAttempts] = useState(3);
  const [minSatisfaction, setMinSatisfaction] = useState(90);
  const [crewIds, setCrewIds] = useState<string[]>([]);

  // ── Skills helpers ──────────────────────────────────────────────

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !skills.includes(s)) {
      setSkills([...skills, s]);
    }
    setSkillInput("");
  };

  const removeSkill = (s: string) => {
    setSkills(skills.filter((x) => x !== s));
  };

  const toggleSkillChip = (s: string) => {
    setSkills(
      skills.includes(s) ? skills.filter((x) => x !== s) : [...skills, s],
    );
  };

  // ── Env helpers ─────────────────────────────────────────────────

  const toggleEnv = (env: EnvType) => {
    setEnvs(
      envs.includes(env) ? envs.filter((e) => e !== env) : [...envs, env],
    );
  };

  // ── Crew helpers ────────────────────────────────────────────────

  const toggleCrew = (id: string) => {
    setCrewIds(
      crewIds.includes(id)
        ? crewIds.filter((c) => c !== id)
        : [...crewIds, id],
    );
  };

  // ── Navigation du stepper ──────────────────────────────────────

  const canGoNext = (): boolean => {
    if (step === 0) return title.trim() !== "" && projectId !== "";
    return true;
  };

  const next = () => {
    if (step < STEPS.length - 1 && canGoNext()) setStep(step + 1);
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  // ── Soumission ─────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!title.trim() || !projectId) return;
    setCreating(true);
    setError(null);
    try {
      // On synchronise aussi les envs sur le projet choisi si nécessaire
      // (les envs choisis servent principalement à l'affichage de la mission)
      const created = await api.missions.create({
        title: title.trim(),
        projectId,
        description: description.trim(),
        status: "idea",
        skills,
        controlMode,
        maxAttempts,
        targetAttempts,
        minSatisfaction,
        crewIds,
        kanbanOrder: null,
      });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de création");
    } finally {
      setCreating(false);
    }
  };

  // ── Projet sélectionné (pour le récap) ─────────────────────────

  const selectedProject = projects.find((p) => p.id === projectId);

  return (
    <div className="modal-overlay show" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "860px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>🚀 Nouvelle idée</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Stepper */}
        <div
          className="stepper"
          style={{ padding: "20px 24px 0" }}
        >
          {STEPS.map((s, i) => (
            <div
              key={s.num}
              className={`step${i === step ? " active" : ""}${i < step ? " done" : ""}`}
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <div className="step-num">{s.num}</div>
              <span className="step-label">{s.label}</span>
              {i < STEPS.length - 1 && <div className="step-line" />}
            </div>
          ))}
        </div>

        {/* Corps */}
        <div
          className="modal-body"
          style={{
            padding: "24px",
            display: "grid",
            gap: "18px",
            overflowY: "auto",
          }}
        >
          {error && (
            <div
              style={{
                padding: "12px",
                borderRadius: "12px",
                background: "rgba(207,107,98,0.1)",
                color: "var(--rust)",
                fontSize: "14px",
              }}
            >
              ⚠ {error}
            </div>
          )}

          {/* ── ÉTAPE 1: IDÉE ───────────────────────────────────── */}
          {step === 0 && (
            <div className="form-card">
              <div className="form-group">
                <label>Titre</label>
                <input
                  className="form-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Setup CI/CD pipeline"
                  autoFocus
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  className="form-textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Objectif de la mission…"
                />
              </div>
              <div className="form-group">
                <label>Projet</label>
                <select
                  className="form-select"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                >
                  {projects.length === 0 && <option value="">— Aucun projet —</option>}
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 2: STRATÉGIE ──────────────────────────────── */}
          {step === 1 && (
            <div className="form-card">
              <div className="form-group">
                <label>Mode de contrôle</label>
                <div className="chip-group">
                  {(["manual", "semi", "auto"] as ControlMode[]).map((mode) => (
                    <span
                      key={mode}
                      className={`chip${controlMode === mode ? " active" : ""}`}
                      onClick={() => setControlMode(mode)}
                    >
                      {mode === "manual"
                        ? "Manuel"
                        : mode === "semi"
                          ? "Semi-auto"
                          : "Auto"}
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Compétences</label>
                <div className="chip-group">
                  {skills.map((s) => (
                    <span key={s} className="chip active" onClick={() => removeSkill(s)}>
                      {s} ✕
                    </span>
                  ))}
                  <input
                    className="form-input"
                    style={{ maxWidth: "200px" }}
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSkill();
                      }
                    }}
                    placeholder="Ajouter + Entrée"
                  />
                  <span className="chip" onClick={addSkill}>+ Ajouter</span>
                </div>
                {/* Suggestions rapides */}
                <div className="chip-group" style={{ marginTop: "8px" }}>
                  {["typescript", "react", "node", "python", "docker", "ci/cd"]
                    .filter((s) => !skills.includes(s))
                    .map((s) => (
                      <span
                        key={s}
                        className="chip"
                        onClick={() => toggleSkillChip(s)}
                      >
                        + {s}
                      </span>
                    ))}
                </div>
              </div>

              <div className="form-group">
                <label>Environnements</label>
                <div className="chip-group">
                  {ENV_OPTIONS.map((env) => (
                    <span
                      key={env}
                      className={`chip${envs.includes(env) ? " active" : ""}`}
                      onClick={() => toggleEnv(env)}
                    >
                      {env.toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 3: PARAMÈTRES ─────────────────────────────── */}
          {step === 2 && (
            <div className="form-card">
              <div className="form-row">
                <div className="form-group">
                  <label>Tentatives max</label>
                  <div className="range-group">
                    <div className="range-display">
                      <span className="range-value">{maxAttempts}</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={maxAttempts}
                      onChange={(e) => setMaxAttempts(Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Tentatives cibles</label>
                  <div className="range-group">
                    <div className="range-display">
                      <span className="range-value">{targetAttempts}</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={10}
                      value={targetAttempts}
                      onChange={(e) => setTargetAttempts(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Satisfaction minimale (%)</label>
                <div className="range-group">
                  <div className="range-display">
                    <span className="range-value">{minSatisfaction}%</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={100}
                    value={minSatisfaction}
                    onChange={(e) => setMinSatisfaction(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Équipage</label>
                <div className="crew-select-grid">
                  {crew.length === 0 && (
                    <div className="no-results">Aucun membre d'équipage</div>
                  )}
                  {crew.map((member) => {
                    const selected = crewIds.includes(member.id);
                    return (
                      <div
                        key={member.id}
                        className={`crew-pick${selected ? " selected" : ""}`}
                        onClick={() => toggleCrew(member.id)}
                      >
                        <div
                          className="crew-pick-icon"
                          style={{ background: member.avatarColor }}
                        >
                          {member.avatar}
                        </div>
                        <div>
                          <div className="crew-pick-name">{member.name}</div>
                          <div className="crew-pick-role">
                            {ROLE_ICONS[member.role]} {ROLE_LABELS[member.role]}
                          </div>
                        </div>
                        <div className="crew-pick-check">✓</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── ÉTAPE 4: VALIDATION ─────────────────────────────── */}
          {step === 3 && (
            <div className="validation-card">
              <div className="validation-row">
                <label>Titre</label>
                <strong>{title || "—"}</strong>
              </div>
              <div className="validation-row">
                <label>Projet</label>
                <strong>{selectedProject?.name ?? "—"}</strong>
              </div>
              {description && (
                <div className="validation-row">
                  <label>Description</label>
                  <strong>{description}</strong>
                </div>
              )}
              <div className="validation-row">
                <label>Mode de contrôle</label>
                <strong>{MODE_LABELS[controlMode]}</strong>
              </div>
              {skills.length > 0 && (
                <div className="validation-row">
                  <label>Compétences</label>
                  <strong>{skills.join(", ")}</strong>
                </div>
              )}
              {envs.length > 0 && (
                <div className="validation-row">
                  <label>Environnements</label>
                  <strong>{envs.map((e) => e.toUpperCase()).join(", ")}</strong>
                </div>
              )}
              <div className="validation-row">
                <label>Tentatives</label>
                <strong>
                  max {maxAttempts} · cible {targetAttempts}
                </strong>
              </div>
              <div className="validation-row">
                <label>Satisfaction min.</label>
                <strong>{minSatisfaction}%</strong>
              </div>
              {crewIds.length > 0 && (
                <div className="validation-row">
                  <label>Équipage</label>
                  <strong>
                    {crewIds
                      .map((id) => crew.find((c) => c.id === id)?.name ?? "")
                      .filter(Boolean)
                      .join(", ")}
                  </strong>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer / Navigation */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "space-between",
            padding: "16px 24px",
            borderTop: "1px solid var(--line)",
          }}
        >
          <button type="button" className="btn" onClick={onClose}>
            Annuler
          </button>
          <div style={{ display: "flex", gap: "10px" }}>
            {step > 0 && (
              <button type="button" className="btn" onClick={prev}>
                ← Précédent
              </button>
            )}
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                className="btn primary"
                onClick={next}
                disabled={!canGoNext()}
              >
                Suivant →
              </button>
            ) : (
              <button
                type="button"
                className="btn primary"
                onClick={handleSubmit}
                disabled={creating || !title.trim() || !projectId}
              >
                {creating ? "Lancement…" : "🚀 Lancer la mission"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
